import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
    CreateProgramItem,
    CreateProgramItemFailure,
    CreateProgramItemSuccess,
    DeleteProgramItem,
    DeleteProgramItemFailure,
    DeleteProgramItemSuccess,
    FetchProgramItem,
    FetchProgramItemFailure,
    FetchProgramItemSuccess,
    SetProgramItem,
    UnsetProgramItem,
    UpdateProgramItem,
    UpdateProgramItemFailure,
    UpdateProgramItemSuccess,
    SetProgramItems,
    AddCourseOfferingsToCourseCategory,
    AddCourseOfferingsToCourseCategorySuccess,
    AddCourseOfferingsToCourseCategoryFailure,
    BookAllParticipantsToCourseExecution,
    BookAllParticipantsToCourseExecutionSuccess,
    BookAllParticipantsToCourseExecutionFailure,
    ChangeProgramCompletionForParticipantsSuccess,
    ChangeProgramCompletionForParticipantsFailure,
    ChangeProgramCompletionForParticipants,
    FetchProgramExecutionStatistics,
    FetchProgramExecutionStatisticsSuccess,
    FetchProgramExecutionStatisticsFailure,
    FetchParticipantProgramExecution,
    FetchParticipantProgramExecutionSuccess,
    FetchParticipantProgramExecutionFailure,
    FetchProgramExecutionMetricsSuccess,
    FetchProgramExecutionMetricsFailure,
    FetchProgramExecutionMetrics,
    FetchRelevantExecutionsDateRange,
    FetchRelevantExecutionsDateRangeSuccess,
    FetchRelevantExecutionsDateRangeFailure,
    UpdateRelevantExecutionsDateRange,
    UpdateRelevantExecutionsDateRangeSuccess,
    UpdateRelevantExecutionsDateRangeFailure,
    DeleteRelevantExecutionsDateRange,
    DeleteRelevantExecutionsDateRangeSuccess,
    DeleteRelevantExecutionsDateRangeFailure,
    UpdateProgramItemRelevantExecutionRule,
    UpdateProgramItemRelevantExecutionRuleSuccess,
    UpdateProgramItemRelevantExecutionRuleFailure,
    FetchProgramExecutionStructureWithDefaultExecutions,
    FetchProgramExecutionStructureWithDefaultExecutionsSuccess,
    FetchProgramExecutionStructureWithDefaultExecutionsFailure,
    FetchProgramExecutionCourseOfferingRelevantExecutions,
    FetchProgramExecutionCourseOfferingRelevantExecutionsSuccess,
    FetchProgramExecutionCourseOfferingRelevantExecutionsFailure,
    FetchProgramParticipantExecutionChanges,
    FetchProgramParticipantExecutionChangesSuccess,
    FetchProgramParticipantExecutionChangesFailure,
    FetchParticipantProgramExecutionGroupSuccess,
    FetchParticipantProgramExecutionGroupFailure,
    FetchParticipantProgramExecutionGroup,
    FetchTeamParticipantProgramExecutionGroup,
    FetchTeamParticipantProgramExecutionGroupSuccess,
    FetchTeamParticipantProgramExecutionGroupFailure,
    FetchTeamParticipantProgramExecution,
    FetchTeamParticipantProgramExecutionSuccess,
    FetchTeamParticipantProgramExecutionFailure,
} from '../actions';
import { AddProgramItems } from '../actions/add-entities.action';
import { ProgramItemApiService } from '../services/program-item.api-service';
import { ProgramItemStateModel } from './program-item.state-model';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { EntityState } from 'app/projects/entity/src/lib/models/entity.state';
import { ProgramItem } from './program-item.model';

@State<ProgramItemStateModel>({
    name: 'programItem',
    defaults: new ProgramItemStateModel(),
})
@Injectable()
export class ProgramItemState extends EntityState<ProgramItem> {
    static sortProperties: string[] = ['name'];

    @Selector()
    static getEntity(state: ProgramItemStateModel, id: string = state.item): ProgramItem {
        if (!state.map) {
            return null;
        }

        return state.map.get(id) || null;
    }

    @Selector()
    static getEntities(state: ProgramItemStateModel): ProgramItem[] {
        if (!state.map) {
            return null;
        }

        const toReturn = Array.from(state.map.values());

        toReturn.sort(MaxBrainUtils.sortCompareFn(ProgramItemState.sortProperties));

        return toReturn;
    }

    constructor(private _programItemApiService: ProgramItemApiService) {
        super(null, 0);
    }

    @Action(AddProgramItems)
    addCourseOfferingsEntities(ctx: StateContext<ProgramItemStateModel>, action: AddProgramItems): void {
        this._addEntities(ctx, action);
    }

    @Action(SetProgramItem)
    setEntity(ctx: StateContext<ProgramItemStateModel>, action: SetProgramItem): void {
        this._setEntity(ctx, action, AddProgramItems);
    }

    @Action(UnsetProgramItem)
    unsetEntity(ctx: StateContext<ProgramItemStateModel>): void {
        this._unsetEntity(ctx);
    }

    @Action(CreateProgramItem)
    createEntity(ctx: StateContext<ProgramItemStateModel>, action: CreateProgramItem): void {
        this._programItemApiService.create(action.payload).subscribe(
            (courseOfferingGroup) => {
                this._addEntities(ctx, { payload: [courseOfferingGroup] });

                ctx.dispatch([new CreateProgramItemSuccess(courseOfferingGroup)]);
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new CreateProgramItemFailure({ response }));
            }
        );
    }

    @Action(UpdateProgramItem)
    updateEntity(ctx: StateContext<ProgramItemStateModel>, action: UpdateProgramItem): void {
        this._updateEntity(ctx, action, UpdateProgramItemSuccess, UpdateProgramItemFailure, AddProgramItems, this._programItemApiService.update(action.payload)).then(() => {});
    }

    @Action(DeleteProgramItem)
    deleteEntity(ctx: StateContext<ProgramItemStateModel>, action: DeleteProgramItem): void {
        this._deleteEntity(ctx, action, DeleteProgramItemSuccess, DeleteProgramItemFailure, this._programItemApiService.delete(action.payload)).then(() => {});
    }

    @Action(FetchProgramItem)
    fetchEntity(ctx: StateContext<ProgramItemStateModel>, action: FetchProgramItem): void {
        this._fetchEntity(ctx, action, FetchProgramItemSuccess, FetchProgramItemFailure, AddProgramItems, SetProgramItem, this._programItemApiService.read(action.payload)).then(
            () => {}
        );
    }

    @Action(FetchProgramItemSuccess)
    fetchEntitySuccess(ctx: StateContext<ProgramItemStateModel>, action: FetchProgramItemSuccess): void {
        this._fetchEntitySuccess(ctx, action);
    }

    @Action(FetchProgramItemFailure)
    fetchEntityFailure(ctx: StateContext<ProgramItemStateModel>, action: FetchProgramItemFailure): void {
        this._fetchEntityFailure(ctx, action);
    }

    @Action(SetProgramItems)
    setCourseOfferingEntities(ctx: StateContext<ProgramItemStateModel>, action: SetProgramItems): void {
        this._setEntities(ctx, action);
    }

    @Action(AddCourseOfferingsToCourseCategory)
    addCourseOfferingsToCourseCategory(ctx: StateContext<ProgramItemStateModel>, { payload }: AddCourseOfferingsToCourseCategory): void {
        this._programItemApiService.addCourseOfferingsToCourseCategory(payload).subscribe(
            (entity) => {
                ctx.dispatch(new AddCourseOfferingsToCourseCategorySuccess(entity));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new AddCourseOfferingsToCourseCategoryFailure());
            }
        );
    }

    @Action(BookAllParticipantsToCourseExecution)
    bookAllParticipantsToCourseExecution(ctx: StateContext<ProgramItemStateModel>, { payload }: BookAllParticipantsToCourseExecution): void {
        this._programItemApiService.bookAllParticipantsToCourseExecution(payload).subscribe(
            (entity) => {
                ctx.dispatch(new BookAllParticipantsToCourseExecutionSuccess({ asyncJobId: String(entity.id) }));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new BookAllParticipantsToCourseExecutionFailure());
            }
        );
    }

    @Action(ChangeProgramCompletionForParticipants)
    changeProgramCompletionForParticipants(ctx: StateContext<ProgramItemStateModel>, { payload }: ChangeProgramCompletionForParticipants): void {
        this._programItemApiService.changeProgramCompletionForParticipants(payload).subscribe(
            (result) => {
                ctx.dispatch(new ChangeProgramCompletionForParticipantsSuccess(result));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new ChangeProgramCompletionForParticipantsFailure());
            }
        );
    }

    @Action(FetchProgramExecutionStatistics)
    fetchProgramExecutionStatistics(ctx: StateContext<ProgramItemStateModel>, { programExecutionId, offeringIds }: FetchProgramExecutionStatistics): void {
        const offeringIdsHash = this._generateHash(offeringIds);

        this._programItemApiService.getProgramExecutionStatistics(programExecutionId, offeringIds).subscribe(
            (entities) => {
                ctx.dispatch(new FetchProgramExecutionStatisticsSuccess(programExecutionId, offeringIdsHash, entities));
            },
            (error: HttpErrorResponse) => {
                ctx.dispatch(new FetchProgramExecutionStatisticsFailure(programExecutionId, offeringIdsHash));
            }
        );
    }

    private _generateHash(offeringIds: string[]): string {
        return offeringIds.sort().join(','); // or use a real hash if needed
    }

    @Action(FetchRelevantExecutionsDateRange)
    fetchRelevantExecutionsDateRange(ctx: StateContext<ProgramItemStateModel>, { id }: FetchRelevantExecutionsDateRange): void {
        this._programItemApiService.getRelevantExecutionsDateRange(id).subscribe(
            (entities) => {
                ctx.dispatch(new FetchRelevantExecutionsDateRangeSuccess(entities));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new FetchRelevantExecutionsDateRangeFailure());
            }
        );
    }

    @Action(UpdateRelevantExecutionsDateRange)
    updateRelevantExecutionsDateRange(ctx: StateContext<ProgramItemStateModel>, { payload }: UpdateRelevantExecutionsDateRange): void {
        this._programItemApiService.updateRelevantExecutionsDateRange(payload.programExecutionId, payload.dateRange).subscribe(
            (entity) => {
                ctx.dispatch(new UpdateRelevantExecutionsDateRangeSuccess(entity));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new UpdateRelevantExecutionsDateRangeFailure());
            }
        );
    }

    @Action(UpdateProgramItemRelevantExecutionRule)
    updateProgramItemRelevantExecutionRule(ctx: StateContext<ProgramItemStateModel>, { payload }: UpdateProgramItemRelevantExecutionRule): void {
        this._programItemApiService.updateProgramItemRelevantExecutionRule(payload.programItemId, payload.relevantExecutionRule).subscribe(
            (entity) => {
                ctx.dispatch(new UpdateProgramItemRelevantExecutionRuleSuccess(entity));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new UpdateProgramItemRelevantExecutionRuleFailure());
            }
        );
    }

    @Action(DeleteRelevantExecutionsDateRange)
    deleteRelevantExecutionsDateRange(ctx: StateContext<ProgramItemStateModel>, { programExecutionId: payload }: DeleteRelevantExecutionsDateRange): void {
        this._programItemApiService.deleteRelevantExecutionsDateRange(payload).subscribe(
            (entity) => {
                ctx.dispatch(new DeleteRelevantExecutionsDateRangeSuccess());
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new DeleteRelevantExecutionsDateRangeFailure(response.error));
            }
        );
    }

    @Action(FetchProgramExecutionMetrics)
    fetchProgramExecutionMetrics(ctx: StateContext<ProgramItemStateModel>, { id }: FetchProgramExecutionMetrics): void {
        this._programItemApiService.getProgramExecutionMetrics(id).subscribe(
            (entities) => {
                ctx.dispatch(new FetchProgramExecutionMetricsSuccess(entities));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new FetchProgramExecutionMetricsFailure());
            }
        );
    }

    @Action(FetchParticipantProgramExecution)
    fetchParticipantProgramExecution(ctx: StateContext<ProgramItemStateModel>, { programCourseId, userId }: FetchParticipantProgramExecution): void {
        this._programItemApiService.getParticipantProgramExecution(programCourseId, userId).subscribe(
            (entities) => {
                ctx.dispatch(new FetchParticipantProgramExecutionSuccess(entities));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new FetchParticipantProgramExecutionFailure());
            }
        );
    }

    @Action(FetchTeamParticipantProgramExecution)
    fetchTeamParticipantProgramExecution(ctx: StateContext<ProgramItemStateModel>, { teamId, programCourseId, userId }: FetchTeamParticipantProgramExecution): void {
        this._programItemApiService.getTeamParticipantProgramExecution(teamId, programCourseId, userId).subscribe(
            (entities) => {
                ctx.dispatch(new FetchTeamParticipantProgramExecutionSuccess(entities));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new FetchTeamParticipantProgramExecutionFailure());
            }
        );
    }

    @Action(FetchParticipantProgramExecutionGroup)
    fetchParticipantProgramExecutionGroup(ctx: StateContext<ProgramItemStateModel>, { programCourseId, courseGroupId, userId }: FetchParticipantProgramExecutionGroup): void {
        this._programItemApiService.getParticipantProgramExecutionGroup(programCourseId, courseGroupId, userId).subscribe(
            (entities) => {
                ctx.dispatch(new FetchParticipantProgramExecutionGroupSuccess(programCourseId, courseGroupId, entities));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new FetchParticipantProgramExecutionGroupFailure(programCourseId, courseGroupId));
            }
        );
    }

    @Action(FetchTeamParticipantProgramExecutionGroup)
    fetchTeamParticipantProgramExecutionGroup(
        ctx: StateContext<ProgramItemStateModel>,
        { teamId, programCourseId, courseGroupId, userId }: FetchTeamParticipantProgramExecutionGroup
    ): void {
        this._programItemApiService.getTeamParticipantProgramExecutionGroup(teamId, programCourseId, courseGroupId, userId).subscribe(
            (entities) => {
                ctx.dispatch(new FetchTeamParticipantProgramExecutionGroupSuccess(programCourseId, courseGroupId, entities));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new FetchTeamParticipantProgramExecutionGroupFailure(programCourseId, courseGroupId));
            }
        );
    }

    @Action(FetchProgramExecutionStructureWithDefaultExecutions)
    fetchProgramExecutionStructureWithDefaultExecutions({ dispatch }: StateContext<ProgramItemStateModel>, { payload }: FetchProgramExecutionStructureWithDefaultExecutions): void {
        this._programItemApiService.getProgramExecutionStructureWithDefaultExecutions(payload.programExecutionId).subscribe(
            (entities) => {
                dispatch(new FetchProgramExecutionStructureWithDefaultExecutionsSuccess(entities));
            },
            (response: HttpErrorResponse) => {
                dispatch(new FetchProgramExecutionStructureWithDefaultExecutionsFailure());
            }
        );
    }

    @Action(FetchProgramExecutionCourseOfferingRelevantExecutions)
    fetchProgramExecutionCourseOfferingRelevantExecutions(
        { dispatch }: StateContext<ProgramItemStateModel>,
        { payload }: FetchProgramExecutionCourseOfferingRelevantExecutions
    ): void {
        this._programItemApiService.getProgramExecutionCourseOfferingRelevantExecutions(payload.programExecutionId, payload.courseOfferingId, payload.context).subscribe(
            (entities) => {
                dispatch(new FetchProgramExecutionCourseOfferingRelevantExecutionsSuccess(entities));
            },
            (response: HttpErrorResponse) => {
                dispatch(new FetchProgramExecutionCourseOfferingRelevantExecutionsFailure());
            }
        );
    }

    @Action(FetchProgramParticipantExecutionChanges)
    fetchParticipantExecutionChanges({ dispatch }: StateContext<ProgramItemStateModel>, { payload }: FetchProgramParticipantExecutionChanges): void {
        this._programItemApiService.getParticipantExecutionChanges(payload.programExecutionId, payload.userIds, payload.courseExecutionIds).subscribe(
            (entities) => {
                dispatch(new FetchProgramParticipantExecutionChangesSuccess(entities));
            },
            (response: HttpErrorResponse) => {
                dispatch(new FetchProgramParticipantExecutionChangesFailure());
            }
        );
    }
}
